import { z } from "zod/v4"
import { DocumentSnapshotLike } from "../firestore"
import { SpaceKey, SpaceKeySchema, SpaceMessageId, SpaceMessageIdSchema, UserId, UserIdSchema } from "../id"
import { CreatedAt } from "./system"

/**
 * 削除を実行した主体を表す型。
 * - system: システムによる自動削除（古いメッセージのクリーンアップ、スペース削除時のカスケード削除など）
 * - user: ユーザーによる削除
 */
export const DeletedBySchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("system").describe("システムによる自動削除"),
  }),
  z.object({
    kind: z.literal("user").describe("ユーザーによる削除"),
    value: UserIdSchema.describe("削除を実行したユーザーのID"),
  }),
])

export type DeletedBy = z.infer<typeof DeletedBySchema>

/**
 * SpaceMessage の削除イベントを表すドキュメント。
 *
 * クライアントはこのコレクションを購読することで、
 * 購読範囲外のメッセージ削除を検知できる。
 */
export const SpaceMessageDeletionDocumentBody = z.object({
  space_key: SpaceKeySchema.describe("削除されたメッセージが属するスペースのID"),
  deleted_by: DeletedBySchema.describe("削除を実行した主体"),
  created_at: CreatedAt.describe("削除日時 (UNIX Timestamp mills)"),
})

export type SpaceMessageDeletionDocumentBody = z.infer<typeof SpaceMessageDeletionDocumentBody>

export const SpaceMessageDeletionDocument = z.intersection(
  SpaceMessageDeletionDocumentBody,
  z.object({
    id: SpaceMessageIdSchema.describe("削除されたメッセージのID（ドキュメントIDと同一）"),
  })
)

export type SpaceMessageDeletionDocument = z.infer<typeof SpaceMessageDeletionDocument>

export class SpaceMessageDeletionDocuments {
  static toDocumentData(doc: SpaceMessageDeletionDocumentBody): object {
    return {
      ...doc,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): SpaceMessageDeletionDocument | null {
    if (!doc) {
      return null
    }

    const data = doc.data()
    if (data === undefined) {
      return null
    }

    const deletedByData = data["deleted_by"]
    const deletedBy: DeletedBy = deletedByData.kind === "system"
      ? { kind: "system" }
      : { kind: "user", value: UserId(deletedByData.value) }

    return {
      id: SpaceMessageId(doc.id),
      space_key: SpaceKey(data["space_key"]),
      deleted_by: deletedBy,
      created_at: data["created_at"],
    }
  }
}
